import { forwardRef } from "react";
import { Alert } from "@mui/material";
import { SnackbarContent, useSnackbar, type CustomContentProps } from "notistack";
import { shadows } from "@karda/design-tokens";

/**
 * Notistack content renderer that keeps the app's established toast look
 * (MUI Alert: rounded 12, soft shadow, blur, centered message).
 */
export const KardaToast = forwardRef<HTMLDivElement, CustomContentProps>(
  function KardaToast({ id, message, variant }, ref) {
    const { closeSnackbar } = useSnackbar();
    const severity = variant === "default" ? "info" : variant;

    return (
      <SnackbarContent ref={ref}>
        <Alert
          onClose={(event) => {
            event.stopPropagation();
            closeSnackbar(id);
          }}
          // The legacy snackbar dismissed on any click (MUI clickaway);
          // notistack has no clickaway, so clicking the toast itself dismisses.
          onClick={() => closeSnackbar(id)}
          severity={severity}
          sx={{
            cursor: "pointer",
            width: "100%",
            fontSize: "16px",
            fontWeight: 500,
            borderRadius: 2,
            boxShadow: shadows.raised,
            backdropFilter: "blur(8px)",
            border: "1px solid rgba(255, 255, 255, 0.2)",
            "& .MuiAlert-message": {
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              textAlign: "center",
              width: "100%",
              // Multi-line messages (e.g. CSV row errors) keep their breaks.
              whiteSpace: "pre-line",
            },
            "& .MuiAlert-icon": { fontSize: "24px" },
          }}
        >
          {message}
        </Alert>
      </SnackbarContent>
    );
  },
);
